/**
 * Citation Parsing Utilities
 *
 * Parses various citation formats from AI responses and transforms them
 * into clean, numbered superscript references with a sources section.
 *
 * Supported formats:
 * - [domain](url) - Standard markdown link
 * - ([domain](url)) - Parenthesized markdown link
 * - [domain.com](url) - Domain with extension
 * - Multiple citations in same sentence
 */

export interface ParsedCitation {
  /** Unique index (1-based) for the citation */
  index: number;
  /** Display name - usually the domain */
  displayName: string;
  /** Full URL */
  url: string;
  /** Original matched text to replace */
  originalText: string;
}

export interface ParsedContent {
  /** Text with citations replaced by superscript markers */
  processedText: string;
  /** Array of unique citations found */
  citations: ParsedCitation[];
}

/**
 * Extracts the domain name from a URL
 */
export function extractDomain(url: string): string {
  try {
    // Handle URLs that don't start with protocol
    const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
    const urlObj = new URL(urlWithProtocol);
    let hostname = urlObj.hostname;

    // Remove 'www.' prefix if present
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }

    return hostname;
  } catch {
    // If URL parsing fails, try to extract domain manually
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/\?#]+)/i);
    return match ? match[1] : url;
  }
}

/**
 * Parses text content to extract citations and replace them with numbered markers
 *
 * Handles formats:
 * - ([domain](url)) - Wrapped in parentheses
 * - [domain](url) - Standard markdown
 * - Multiple inline citations
 */
export function parseCitations(text: string): ParsedContent {
  if (!text || typeof text !== 'string') {
    return { processedText: text || '', citations: [] };
  }

  const citations: ParsedCitation[] = [];
  const seenUrls = new Map<string, number>(); // URL -> citation index

  // Pattern to match both wrapped and unwrapped citation formats
  // Matches: ([text](url)) or [text](url)
  // Also handles trailing punctuation and UTM parameters
  const citationPattern = /\(?\[([^\]]+)\]\((https?:\/\/[^\s\)]+)\)\)?/g;

  let processedText = text;
  let match;

  // First pass: collect all unique citations
  while ((match = citationPattern.exec(text)) !== null) {
    const [fullMatch, linkText, url] = match;

    // Clean the URL (remove trailing parentheses that might be part of the match)
    const cleanUrl = url.replace(/\)+$/, '');

    // Check if we've seen this URL before
    if (!seenUrls.has(cleanUrl)) {
      const index = citations.length + 1;
      seenUrls.set(cleanUrl, index);

      // Use the link text or extract domain from URL
      const displayName = extractDomain(cleanUrl);

      citations.push({
        index,
        displayName,
        url: cleanUrl,
        originalText: fullMatch
      });
    }
  }

  // Second pass: replace citations with superscript markers
  // We need to do this carefully to handle overlapping matches
  processedText = text.replace(citationPattern, (fullMatch, linkText, url) => {
    const cleanUrl = url.replace(/\)+$/, '');
    const citationIndex = seenUrls.get(cleanUrl);

    if (citationIndex !== undefined) {
      // Return superscript marker - use Unicode superscript numbers for cleaner display
      return `[${citationIndex}]`;
    }
    return fullMatch;
  });

  return {
    processedText,
    citations
  };
}

/**
 * Formats text with basic markdown support (bold, italic, bullet points)
 * while preserving citation markers
 */
export function formatTextWithMarkdown(text: string): string {
  if (!text) return '';

  let formatted = text;

  // Bold: **text** or __text__
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not within words)
  formatted = formatted.replace(/(?<![*\w])\*([^*]+)\*(?![*\w])/g, '<em>$1</em>');
  formatted = formatted.replace(/(?<![_\w])_([^_]+)_(?![_\w])/g, '<em>$1</em>');

  return formatted;
}

/**
 * Converts superscript marker [n] to actual superscript HTML
 */
export function convertToSuperscript(text: string): string {
  // Convert [1], [2], etc. to superscript
  return text.replace(/\[(\d+)\]/g, '<sup class="citation-marker">[$1]</sup>');
}

/**
 * Complete processing pipeline: parse citations, format markdown, convert superscripts
 */
export function processMessageContent(text: string): ParsedContent {
  const { processedText, citations } = parseCitations(text);
  return {
    processedText,
    citations
  };
}
